import styled from "@emotion/styled";
import { SelectChangeEvent } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  CameraIcon,
  TrashCanIcon,
  WarningIcon,
} from "../assets/icons/icons";
import useGetMobilityDetail from "../hooks/useGetMobilityDetail";
import usePutMobility from "../hooks/usePutMobility";
import userStorage from "../utils/userStorage";
import AlertModal from "./alertModal";
import SelectBox, { menuItemType } from "./selectBox";
import theme from "../theme";
import useGetAllCustomer from "../hooks/useGetAllCustomer";
import Spinner from "./spinner";
import { isOnlyNumber } from "../utils/util";
import { CustomAlert } from "./customAlert";
import { mobileOptions } from "../utils/constants";
import { Button } from "./common/Button";
import { ModalLayout } from "./common/ModalLayout";
import { useSnackbar } from "notistack";
import { useWindowSize } from "@reactuses/core";
import { SystemSetting } from "../types/types";
import { deleteMobileImage, putMobileImage } from "../api/mobility";
import { deviceImageArray } from "../utils/deviceImageArray";
import { Label } from "./common/Label";
import { TextInput } from "./common/TextInput";
import { InstallImages } from "./InstallImages";
import { useI18n } from "../locales";

const { sm, md, lg } = theme.screen;

type Params = {
  mobility_image: string;
  mobility_name: string;
  phone: string;
  mobility_type_code: string;
  device_pid: string;
  device_phone: string | null;
};

const MobilityDetailModal: React.FC<{
  open: boolean;
  seq: string | null;
  onClickCancel: () => void;
  onClose: any;
  systemSetting: SystemSetting | null;
}> = (props) => {
  const { t } = useI18n();
  const { open, seq, onClickCancel, onClose, systemSetting } = props;
  const isOnPremise = process.env.REACT_APP_ROUTE_ID; // 온프레미스 여부 (true면 IMEI 항목 지우고 device_phone에 초기값 지정)
  const { width } = useWindowSize();
  const { enqueueSnackbar } = useSnackbar();
  const fileUploadRef = useRef<HTMLInputElement>(null);
  const { data, isLoading } = useGetMobilityDetail(
    { idx: Number(seq) },
    open
  );
  const [disabled, setDisabled] = useState(true);
  const [openCancelmModal, setOpenCancelModal] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [imgFile, setImgFile] = useState("");
  const [imgData, setImgData] = useState<any>();
  const [imgCancel, setImgCancel] = useState(false);
  const [input, setInput] = useState<Params>({
    mobility_name: data?.mobility_name ?? "",
    mobility_type_code: data?.mobility_type ?? "0",
    device_phone: isOnPremise
      ? "000000000000000"
      : data?.device_phone ?? "",
    device_pid: data?.device_pid ?? "",
    phone: data?.owner_phone ?? "",
    mobility_image: data?.mobility_image ?? "",
  });
  const queryClient = useQueryClient();

  const user = userStorage.get();

  const handleFileUpload = () => {
    if (fileUploadRef.current) {
      fileUploadRef.current.click();
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setInput((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSelectBox = (event: SelectChangeEvent) => {
    setInput((prevState) => {
      return {
        ...prevState,
        mobility_type_code: event.target.value.toString(),
      };
    });

    if (Number(event.target.value) !== 0) {
      // 기기 종류가 기타가 아니면 기존 이미지 지우기
      handleFileCancel();
    }
  };

  const onClickModifyBtn = () => {
    setDisabled(false);
  };

  const handleFileInputChange = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        if (reader.result !== null) {
          setImgFile(reader.result as string);
        }
      };

      setImgData(file); // 테스트
    }
  };

  const handleFileCancel = () => {
    setInput((prevState) => ({
      ...prevState,
      mobility_image: "",
    }));
    setImgFile("");
    setImgData("");
    setImgCancel(true);
  };

  const { mutate: patchMobility, isPending } = usePutMobility();

  const handlePatch = async () => {
    patchMobility(
      {
        device_seq: Number(seq),
        mobility_name: input.mobility_name,
        device_phone: input.device_phone,
        device_pid: input.device_pid,
        mobility_type_code: Number(input.mobility_type_code),
        phone: input.phone,
      },
      {
        onSuccess: async (mobilityResponse) => {
          let imageSuccess = true; // 이미지 작업 성공 여부
          let imageMessage = ""; // 이미지 작업 메시지

          try {
            if (imgData) {
              // 이미지 업로드
              const formData = new FormData();
              formData.append("pid", input.device_pid as string);
              formData.append("file", imgData);

              const response = await putMobileImage(formData);
              if (response.code === 2002) {
                imageMessage = response.message;
              } else {
                imageSuccess = false;
                imageMessage = response.message;
              }
            } else if (data?.mobility_image && !imgData && imgCancel) {
              // 이미지 삭제
              const response = await deleteMobileImage({
                pid: input.device_pid as string,
              });
              if (response.code === 2002) {
                imageMessage = response.message;
              } else {
                imageSuccess = false;
                imageMessage = response.message;
              }
            }
          } catch (error) {
            imageSuccess = false;
            imageMessage = "이미지 작업 중 오류가 발생했습니다.";
          }

          // 메시지 처리
          enqueueSnackbar(mobilityResponse.message, {
            variant: "success",
          });

          if (imageMessage) {
            enqueueSnackbar(imageMessage, {
              variant: imageSuccess ? "success" : "error",
            });
          }

          // 최종 정리 및 초기화
          finalizePatch();
        },
      }
    );
  };

  const finalizePatch = () => {
    setImgFile("");
    setImgData("");
    queryClient.invalidateQueries({ queryKey: ["Mobility"] });
    queryClient.invalidateQueries({ queryKey: ["MobilityDetail"] });
    onClickCancel();
    setDisabled(true);
  };

  useEffect(() => {
    if (data) {
      setInput((prevState) => ({
        ...prevState,
        device_phone: isOnPremise ? "000000000000000" : data.device_phone,
        device_pid: data.device_pid,
        mobility_name: data.mobility_name,
        mobility_type_code: data.mobility_type,
        phone: data.owner_phone,
        mobility_image: data.mobility_image ? data.mobility_image : "",
        registered_at: data.registered_at ? data.registered_at : "",
      }));
    }
  }, [data, setInput]);

  // 소유자 리스트 가져오기
  const { data: AllCustomer } = useGetAllCustomer({
    page: 0,
    searchKey: "",
    area: "",
    limit: -1,
  });
  let relatedIdListType: menuItemType[] = [
    {
      value: -1,
      title: t.deviceDetail.ownerPlaceholder,
    },
  ];
  if (AllCustomer) {
    AllCustomer.list.filter((item) => {
      relatedIdListType.push({
        value: item.id,
        title: item.seq + ". " + item.name,
      });
    });
  }

  const handleSelectBoxUser = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    if (value == "-1") {
      setOpenAlert(true);
      return;
    } else {
      setInput((prevState) => ({
        ...prevState,
        phone: event.target.value,
      }));
    }
  };

  const isDevicePidErr = !isOnlyNumber(input.device_pid);
  const isDevicePhoneErr = !isOnlyNumber(input.device_phone ?? "");

  // 필수입력값들
  const isAllFilled =
    [
      input.mobility_name,
      input.mobility_type_code,
      input.device_pid,
      input.device_phone,
    ].every((el) => el !== "") &&
    input.phone !== "-1" &&
    input.device_pid.length >= 8 &&
    input.device_phone &&
    input.device_phone.length >= 15 &&
    !isDevicePidErr &&
    !isDevicePhoneErr;

  const closeToModal = () => {
    setOpenCancelModal(false);
    onClose();
    setDisabled(true);
    setImgFile("");
    setImgData("");

    if (data) {
      setInput((prevState) => ({
        ...prevState,
        address: data?.address,
        mobility_name: data?.mobility_name,
        address_detail: data?.owner_addr,
        device_phone: data?.device_phone,
        device_pid: data?.device_pid,
        mobility_type_code: data?.mobility_type,
        name: data?.owner_name,
        nok_name: data?.nok_name,
        nok_phone: data?.nok_phone,
        phone: data?.owner_phone,
      }));
    }
    onClickCancel();
  };

  // console.log("데이터", data);
  // console.log("인풋", input);
  // console.log(imgData);

  return (
    <ModalLayout
      open={open}
      onClose={() => {
        if (disabled) {
          onClose();
        } else if (!disabled) {
          setOpenCancelModal(true);
        }
      }}
      width="550px"
      title={disabled ? t.deviceDetail.title : t.deviceDetail.titleEdit}
    >
      {isLoading && <Spinner />}
      {data && (
        <>
          <Body>
            <input
              style={{ display: "none" }}
              type="file"
              ref={fileUploadRef}
              accept=".jpg,.jpeg,.png"
              onChange={handleFileInputChange}
            />
            <MiddleBody>
              <ImageWrapper
                style={{
                  position: "relative",
                  background:
                    input?.mobility_image === "" && imgFile === ""
                      ? theme.color.white
                      : theme.color.neutral_50,
                }}
              >
                {input?.mobility_image === "" && imgFile === "" ? (
                  <CustomImg
                    src={`${process.env.REACT_APP_BASE_URL}/img/${
                      deviceImageArray.find(
                        (list) => list.code === input.mobility_type_code
                      )?.detailFileName
                    }`}
                    alt="농기계 이미지"
                  />
                ) : (
                  <CustomImg
                    src={
                      imgFile
                        ? imgFile
                        : `${process.env.REACT_APP_BASE_URL}/${input?.mobility_image}`
                    }
                    alt="농기계 이미지"
                  />
                )}
                {!disabled && (input?.mobility_image || imgFile) ? (
                  <IconWrapper
                    top={"15px"}
                    right={"70px"}
                    onClick={handleFileCancel}
                  >
                    <TrashCanIcon />
                  </IconWrapper>
                ) : null}
                {input.mobility_type_code === "0" && (
                  <IconWrapper
                    top={"15px"}
                    right={"15px"}
                    style={
                      disabled ? { display: "none" } : { display: "flex" }
                    }
                    onClick={() => {
                      if (!disabled) {
                        handleFileUpload();
                      } else return;
                    }}
                  >
                    <CameraIcon />
                  </IconWrapper>
                )}
              </ImageWrapper>
              <InputWrapper>
                <Label text={t.deviceDetail.deviceName} htmlFor="mobility_name" />
                <TextInput
                  placeholder="최대 20글자"
                  onChange={handleChange}
                  width={"370px"}
                  id="mobility_name"
                  name="mobility_name"
                  value={
                    disabled ? data?.mobility_name : input.mobility_name
                  }
                  maxLength={20}
                  disabled={disabled}
                />
              </InputWrapper>
              <InputWrapper>
                <Label text={t.deviceDetail.owner} htmlFor="name" />

                <SelectBox
                  filter={input?.phone}
                  menuItems={relatedIdListType}
                  width={width <= md ? `100%` : 370}
                  disabled={disabled}
                  handleChange={handleSelectBoxUser}
                  mr={"0px"}
                  srLabel={t.deviceDetail.ownerSelect}
                />
              </InputWrapper>

              <InputWrapper>
                <Label text={t.deviceDetail.registrationDate} htmlFor="registered_at" />
                <TextInput
                  disabled={true}
                  width={"370px"}
                  id="registered_at"
                  name="registered_at"
                  defaultValue={data?.registered_at}
                />
              </InputWrapper>
              <InputWrapper>
                <Label text={t.deviceDetail.deviceType} htmlFor="kindof" />

                <SelectBox
                  filter={
                    input.mobility_type_code
                      ? Number(input.mobility_type_code)
                      : 18
                  }
                  disabled={disabled}
                  handleChange={handleSelectBox}
                  menuItems={mobileOptions}
                  width={width <= md ? `100%` : 370}
                  mr={"0px"}
                  srLabel={t.deviceDetail.deviceType}
                />
              </InputWrapper>
              <InputWrapper
                style={{
                  position: "relative",
                }}
              >
                <Label text={t.deviceDetail.terminalSerialNumber} htmlFor="device_pid" />
                <TextInput
                  onChange={handleChange}
                  disabled={disabled}
                  width={"370px"}
                  id="device_pid"
                  name="device_pid"
                  value={disabled ? data?.device_pid : input.device_pid}
                  placeholder="기본 8자 ~ 최대 16자"
                  maxLength={16}
                />
                {!disabled && isDevicePidErr && (
                  <PwError>{t.deviceDetail.numbersOnly}</PwError>
                )}
              </InputWrapper>
              {!isOnPremise && (
                <InputWrapper
                  style={{
                    position: "relative",
                  }}
                >
                  <Label text={t.deviceDetail.terminalIMEI} htmlFor="device_phone" />

                  <TextInput
                    onChange={handleChange}
                    disabled={disabled}
                    width={"370px"}
                    id="device_phone"
                    name="device_phone"
                    value={
                      (disabled
                        ? data?.device_phone
                        : input.device_phone) ?? ""
                    }
                    placeholder="숫자 15자리"
                    maxLength={15}
                  />
                  {!disabled && isDevicePhoneErr && (
                    <PwError>{t.deviceDetail.numbersOnly}</PwError>
                  )}
                </InputWrapper>
              )}
            
              <InputWrapper
                style={{
                  position: "relative",
                }}
              >
                <Label text={t.deviceDetail.installPhotos} htmlFor="install_image" />
                <InstallImages
                  mobility_full_image_url={
                    data.picture1_url
                  }
                  device_install_image_url={
                    data.picture2_url
                  }
                  device_serial_image_url={
                    data.picture3_url
                  }
                  power_cable_image_url={data.picture4_url}
                  mobility_sign_image_url={
                    data.picture5_url
                  }
                  user_image_url={data.picture6_url}
                />
              </InputWrapper>
            </MiddleBody>
          </Body>
          <BtnWrapper>
            {user?.permission?.auth_modify === "Y" && (
              <Button
                onClick={() => {
                  if (disabled) {
                    onClickModifyBtn();
                  } else {
                    handlePatch();
                  }
                }}
                bgColor={theme.statusColor.MAIN}
                hoverColor={theme.statusColor.MAIN_HOVER}
                isDisabled={
                  (!disabled && !isAllFilled) || !systemSetting?.update
                }
                isLoading={isPending}
              >
                {width <= md && (disabled ? t.deviceDetail.modifyDevice : t.deviceDetail.modifyComplete)}
                {width > md &&
                  (disabled ? t.deviceDetail.modifyDevice : t.deviceDetail.modifyComplete)}
              </Button>
            )}
            <Button
              type="button"
              onClick={() => {
                if (disabled) {
                  onClickCancel();
                  setDisabled(true);
                  setImgFile("");
                  setImgData("");
                } else {
                  setOpenCancelModal(true);
                }
              }}
              bgColor={theme.statusColor.CLOSE}
              hoverColor={theme.statusColor.CLOSE_HOVER}
            >
              {disabled ? t.common.close : t.deviceDetail.reset}
            </Button>
          </BtnWrapper>

          <AlertModal
            open={openCancelmModal}
            onPressNo={() => setOpenCancelModal(false)}
            title={t.deviceDetail.cancelModify}
            content={t.deviceDetail.cancelModifyContent}
            confirmText={t.common.confirm}
            icon={<WarningIcon />}
            onPressYes={closeToModal}
          />
        </>
      )}
      <CustomAlert
        open={openAlert}
        onPressOK={() => setOpenAlert(false)}
        title={t.deviceDetail.ownerRequired}
        content={t.deviceDetail.ownerError}
        confirmText={t.common.confirm}
        icon={<WarningIcon />}
      />
    </ModalLayout>
  );
};

const Body = styled.div`
  background: ${theme.color.neutral_50};
  border-radius: 10px;
  padding-left: 40px;
  padding-right: 30px;
  padding-top: 30px;
  padding-bottom: 30px;
  width: 100%;
  display: flex;
  flex-wrap:wrap;
  gap: 44px;
  margin-bottom: 40px;

  @media (max-width: ${sm}px) {
    padding: 10px;
    padding-bottom: 30px;
  }
`;

const MiddleBody = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 25px;
`;

const ImageWrapper = styled.div`
  width: 100%;
  max-width: 400px;
  border: 3px solid ${theme.color.neutral_200};
  display: flex;
  border-radius: 15px;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;

  @media (min-width: ${lg + 1}px) {
    width: 400px;
  }
`;

const IconWrapper = styled.button<{ top: string; right: string }>`
  width: 43px;
  height: 43px;
  background: ${theme.color.white};
  border-radius: 50%;
  box-shadow: 0px 1.0705px 4.282px rgba(0, 0, 0, 0.2);
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: ${({ top }) => top};
  right: ${({ right }) => right};
  cursor: pointer;
  &:hover {
    background: ${theme.color.neutral_100};
    box-shadow: 0px 1.0705px 10px rgba(0, 0, 0, 0.2);
  }
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: ${sm}px) {
    width: 100%;
  }
  @media (min-width: ${sm + 1}px) and (max-width: ${md}px) {
    width: 100%;
  }
  @media (min-width: ${md + 1}px) and (max-width: ${lg}px) {
    width: auto;
  }
`;

const BtnWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin: 0 50px;
  margin-top: 20px;

  button {
    width: 270px;
    height: 50px;
  }

  @media (max-width: ${md}px) {
    margin: 0px;
    margin-top: 20px;

    button {
      height: 30px;
    }
  }
`;

const CustomImg = styled.img`
  width: 90%;
  min-height: 200px;
  max-height: 270px;
  object-fit: contain;

  @media (min-width: ${lg + 1}px) {
    width: 247px;
    height: 270px;
  }
`;

const PwError = styled.p`
  font-size: 10px;
  color: ${theme.color.red_500};
  padding: 5px;
  position: absolute;
  bottom: -23px;
  left: 3px;
`;

export default MobilityDetailModal;
